import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { pricingService } from '../services/pricingService';
import { providersService } from '../services/providersService';
import { vehicleTypesService } from '../services/vehicleTypesService';
import { customersService } from '../services/customersService';
import {
  Pricing,
  PricingType,
  PricingDirection,
  Provider,
  VehicleType,
  Customer,
  UserRole,
} from '../types';

type TabType = 'provider' | 'customer';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeTab, setActiveTab] = useState<TabType>('provider');
  const [pricings, setPricings] = useState<Pricing[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState<Pricing | null>(null);

  const [formData, setFormData] = useState({
    pricingType: PricingType.PROVIDER,
    providerId: '',
    customerId: '',
    pickupLocation: '',
    dropoffLocation: '',
    direction: PricingDirection.BOTH,
    vehicleTypeId: '',
    price: '',
    currency: 'TRY',
  });

  const canManagePricing =
    user?.role === UserRole.PLATFORM_ADMIN ||
    user?.role === UserRole.ORG_ADMIN ||
    user?.role === UserRole.MANAGER;

  useEffect(() => {
    loadBaseData();
  }, []);

  useEffect(() => {
    loadPricings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadBaseData = async () => {
    try {
      const [providersData, vehicleTypesData, customersData] = await Promise.all([
        providersService.getAll(),
        vehicleTypesService.getAll(),
        customersService.getAll(true),
      ]);
      setProviders(providersData);
      setVehicleTypes(vehicleTypesData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to load base data:', error);
    }
  };

  const loadPricings = async () => {
    try {
      setLoading(true);
      const type = activeTab === 'provider' ? PricingType.PROVIDER : PricingType.CUSTOMER;
      const data = await pricingService.getAll(type);
      setPricings(data);
    } catch (error) {
      toast.error(t('pricing.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (pricing?: Pricing) => {
    if (pricing) {
      setEditingPricing(pricing);
      setFormData({
        pricingType: pricing.pricingType,
        providerId: pricing.providerId || '',
        customerId: pricing.customerId || '',
        pickupLocation: pricing.pickupLocation,
        dropoffLocation: pricing.dropoffLocation,
        direction: pricing.direction,
        vehicleTypeId: pricing.vehicleTypeId || '',
        price: pricing.price.toString(),
        currency: pricing.currency,
      });
    } else {
      setEditingPricing(null);
      setFormData({
        pricingType: activeTab === 'provider' ? PricingType.PROVIDER : PricingType.CUSTOMER,
        providerId: '',
        customerId: '',
        pickupLocation: '',
        dropoffLocation: '',
        direction: PricingDirection.BOTH,
        vehicleTypeId: '',
        price: '',
        currency: 'TRY',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        pricingType: formData.pricingType,
        providerId: formData.providerId || undefined,
        customerId: formData.customerId || undefined,
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.dropoffLocation,
        direction: formData.direction,
        vehicleTypeId: formData.vehicleTypeId || undefined,
        price: parseFloat(formData.price),
        currency: formData.currency,
      };

      if (editingPricing) {
        await pricingService.update(editingPricing.id, data);
        toast.success(t('pricing.priceUpdated'));
      } else {
        await pricingService.create(data);
        toast.success(t('pricing.priceAdded'));
      }
      setShowModal(false);
      loadPricings();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('pricing.operationFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('pricing.deleteConfirm'))) return;
    try {
      await pricingService.delete(id);
      toast.success(t('pricing.priceDeleted'));
      loadPricings();
    } catch (error) {
      toast.error(t('pricing.deleteFailed'));
    }
  };

  const getDirectionLabel = (direction: PricingDirection) => {
    switch (direction) {
      case PricingDirection.ARRIVAL: return t('pricing.directionArrival');
      case PricingDirection.DEPARTURE: return t('pricing.directionDeparture');
      case PricingDirection.BOTH: return t('pricing.directionBoth');
    }
  };

  if (!canManagePricing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.accessDenied')}</h2>
          <p className="text-gray-600">{t('pricing.noPermission')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('pricing.title')}</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">{t('pricing.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('provider')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            activeTab === 'provider'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Tedarikçi Fiyatları
        </button>
        <button
          onClick={() => setActiveTab('customer')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            activeTab === 'customer'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          Müşteri Fiyatları
        </button>
      </div>

      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[44px] font-medium"
        >
          + {activeTab === 'provider' ? 'Tedarikçi Fiyatı Ekle' : 'Müşteri Fiyatı Ekle'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">{t('common.loading')}</div>
      ) : pricings.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">{t('pricing.noPrices')}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {pricings.map((pricing) => (
              <div key={pricing.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {activeTab === 'provider' ? pricing.provider?.name : pricing.customer?.name} {' '}
                      {!pricing.provider?.name && !pricing.customer?.name && '-'}
                    </p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                      pricing.direction === PricingDirection.ARRIVAL
                        ? 'bg-green-100 text-green-800'
                        : pricing.direction === PricingDirection.DEPARTURE
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getDirectionLabel(pricing.direction)}
                    </span>
                  </div>
                  <p className="font-bold text-green-600 text-lg">
                    {pricing.price} {pricing.currency}
                  </p>
                </div>
                <div className="mb-3 text-sm">
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-500">{t('pricing.from')}:</span> {pricing.pickupLocation}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-500">{t('pricing.to')}:</span> {pricing.dropoffLocation}
                  </p>
                  {pricing.vehicleType && (
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="text-gray-500">{t('pricing.vehicleType')}:</span> {pricing.vehicleType.name}
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => handleOpenModal(pricing)} className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-900 font-medium min-h-[36px]">
                    {t('common.edit')}
                  </button>
                  <button onClick={() => handleDelete(pricing.id)} className="px-3 py-1.5 text-sm text-red-600 hover:text-red-900 font-medium min-h-[36px]">
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <table className="hidden md:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  {activeTab === 'provider' ? t('pricing.provider') : 'Müşteri'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('pricing.from')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('pricing.to')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('pricing.direction')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('pricing.vehicleType')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('pricing.price')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {pricings.map((pricing) => (
                <tr key={pricing.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {activeTab === 'provider' ? pricing.provider?.name : pricing.customer?.name || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{pricing.pickupLocation}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{pricing.dropoffLocation}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{getDirectionLabel(pricing.direction)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{pricing.vehicleType?.name || t('pricing.allVehicles')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">{pricing.price} {pricing.currency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => handleOpenModal(pricing)} className="text-blue-600 hover:text-blue-900 mr-3">{t('common.edit')}</button>
                    <button onClick={() => handleDelete(pricing.id)} className="text-red-600 hover:text-red-900">{t('common.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {editingPricing ? t('pricing.editPrice') : (activeTab === 'provider' ? 'Tedarikçi Fiyatı Ekle' : 'Müşteri Fiyatı Ekle')}
                </h2>
                <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl p-1 min-w-[44px] min-h-[44px] flex items-center justify-center">×</button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">

              {/* Provider or Customer Select */}
              {formData.pricingType === PricingType.PROVIDER ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('pricing.provider')} *
                  </label>
                  <select
                    value={formData.providerId}
                    onChange={(e) => setFormData({ ...formData, providerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                    required
                  >
                    <option value="">{t('pricing.select')}</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Müşteri *
                  </label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                    required
                  >
                    <option value="">Müşteri seçin</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Pickup Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pricing.from')} ({t('pricing.pickup')}) *
                </label>
                <input
                  type="text"
                  value={formData.pickupLocation}
                  onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                  placeholder="örn. Sabiha Gökçen Havalimanı"
                  required
                />
              </div>

              {/* Dropoff Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pricing.to')} ({t('pricing.dropoff')}) *
                </label>
                <input
                  type="text"
                  value={formData.dropoffLocation}
                  onChange={(e) => setFormData({ ...formData, dropoffLocation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                  placeholder="örn. Taksim Oteli"
                  required
                />
              </div>

              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pricing.direction')}
                </label>
                <select
                  value={formData.direction}
                  onChange={(e) => setFormData({ ...formData, direction: e.target.value as PricingDirection })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                >
                  <option value={PricingDirection.BOTH}>{t('pricing.directionBoth')}</option>
                  <option value={PricingDirection.ARRIVAL}>{t('pricing.directionArrivalOnly')}</option>
                  <option value={PricingDirection.DEPARTURE}>{t('pricing.directionDepartureOnly')}</option>
                </select>
              </div>

              {/* Vehicle Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pricing.vehicleTypeOptional')}
                </label>
                <select
                  value={formData.vehicleTypeId}
                  onChange={(e) => setFormData({ ...formData, vehicleTypeId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                >
                  <option value="">{t('pricing.allVehicles')}</option>
                  {vehicleTypes.map((vt) => (
                    <option key={vt.id} value={vt.id}>{vt.name}</option>
                  ))}
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('pricing.price')} *
                </label>
                <div className="flex">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-l-lg"
                    placeholder="0.00"
                    required
                  />
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white rounded-r-lg"
                  >
                    <option value="TRY">TRY</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[44px] font-medium"
                >
                  {editingPricing ? t('common.update') : t('common.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
